import { useRef, useState } from 'react';
import { ClipboardCheck, ImagePlus, X, RefreshCw, Loader2, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { getUebergabeFotoStoragePath } from '@/lib/mietwagen';
import InputField from './InputField';
import type { WizardDaten } from '@/lib/mietwagen-wizard-validation';

interface Props {
  daten: WizardDaten;
  onChange: (updated: Partial<WizardDaten>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  akte?: Record<string, any> | null;
}

interface FailedUpload {
  file: File;
  name: string;
}

function safeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export default function Step5Uebergabe({ daten, onChange, akte }: Props) {
  const set = (field: keyof WizardDaten, value: unknown) => onChange({ [field]: value });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);

  async function uploadFoto(file: File): Promise<string | null> {
    if (!akte?.id && !akte?.akten_nummer) {
      toast.error('Foto-Upload nicht möglich: Akte noch nicht gespeichert.');
      return null;
    }

    const filename = `${Date.now()}_${safeFilename(file.name)}`;
    const storagePath = getUebergabeFotoStoragePath(akte, filename);

    setUploadingFiles(prev => [...prev, file.name]);
    setFailedUploads(prev => prev.filter(f => f.name !== file.name));

    try {
      const { error } = await supabase.storage
        .from('akten-dokumente')
        .upload(storagePath, file, { contentType: file.type || 'image/jpeg', upsert: false });

      if (error) {
        toast.error(`Upload fehlgeschlagen: ${file.name}`);
        setFailedUploads(prev => [...prev, { file, name: file.name }]);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('akten-dokumente')
        .getPublicUrl(storagePath);

      return urlData.publicUrl;
    } finally {
      setUploadingFiles(prev => prev.filter(n => n !== file.name));
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    const results = await Promise.all(files.map(f => uploadFoto(f)));
    const newUrls = results.filter((u): u is string => u !== null);
    if (newUrls.length > 0) {
      onChange({ uebergabe_fotos_urls: [...(daten.uebergabe_fotos_urls ?? []), ...newUrls] });
    }
  }

  function removeFoto(url: string) {
    onChange({ uebergabe_fotos_urls: daten.uebergabe_fotos_urls.filter(u => u !== url) });
  }

  const canUpload = !!(akte?.id || akte?.akten_nummer);

  return (
    <div className="space-y-5 p-5">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="w-4 h-4 text-orange-600" />
        <h3 className="text-sm font-semibold">Übergabe-Protokoll</h3>
        <span className="text-[11px] text-gray-400">(optional)</span>
      </div>

      <p className="text-[12px] text-gray-500">
        Alle Felder sind optional. Das Protokoll kann auch ohne diese Angaben erstellt werden.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Fahrzeugübergabe */}
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 space-y-3">
          <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">Fahrzeugübergabe</p>
          <InputField label="Datum" value={daten.uebergabe_datum} onChange={v => set('uebergabe_datum', v)} type="date" />
          <InputField label="Uhrzeit" value={daten.uebergabe_uhrzeit} onChange={v => set('uebergabe_uhrzeit', v)} type="time" />
          <InputField label="Ort" value={daten.uebergabe_ort} onChange={v => set('uebergabe_ort', v)} />
          <InputField label="KM-Stand" value={daten.uebergabe_km} onChange={v => set('uebergabe_km', v)} type="number" placeholder="z. B. 45280" />
          <InputField label="Tankstand" value={daten.uebergabe_tank} onChange={v => set('uebergabe_tank', v)} placeholder="z. B. 3/4" />
        </div>

        {/* Fahrzeugrückgabe */}
        <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 space-y-3">
          <p className="text-[11px] font-bold text-purple-700 uppercase tracking-wide">Fahrzeugrückgabe</p>
          <InputField label="Datum" value={daten.rueckgabe_datum} onChange={v => set('rueckgabe_datum', v)} type="date" />
          <InputField label="Ort" value={daten.rueckgabe_ort} onChange={v => set('rueckgabe_ort', v)} />
          <InputField label="KM-Stand" value={daten.rueckgabe_km} onChange={v => set('rueckgabe_km', v)} type="number" placeholder="z. B. 45280" />
          <InputField label="Tankstand" value={daten.rueckgabe_tank} onChange={v => set('rueckgabe_tank', v)} placeholder="z. B. 3/4" />
        </div>
      </div>

      {/* Vorschäden */}
      <div>
        <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
          Vorhandene Schäden / Vorschäden (optional)
        </label>
        <textarea
          value={daten.uebergabe_vorschaeden}
          onChange={e => set('uebergabe_vorschaeden', e.target.value)}
          rows={3}
          placeholder="z. B. Kratzer vorne links, Delle Fahrertür …"
          className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none resize-y"
        />
      </div>

      {/* Foto-Upload */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide">
            Fotos Übergabe (optional)
          </label>
          {daten.uebergabe_fotos_urls.length > 0 && (
            <span className="text-[10px] text-gray-400">{daten.uebergabe_fotos_urls.length} Foto(s)</span>
          )}
        </div>

        {/* Upload-Button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          disabled={!canUpload}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canUpload || uploadingFiles.length > 0}
          className={[
            'flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed text-sm transition-all w-full justify-center',
            canUpload
              ? 'border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50'
              : 'border-gray-200 text-gray-400 cursor-not-allowed',
          ].join(' ')}
        >
          {uploadingFiles.length > 0 ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Wird hochgeladen…
            </>
          ) : (
            <>
              <ImagePlus className="w-4 h-4" />
              Fotos hinzufügen
            </>
          )}
        </button>

        {!canUpload && (
          <p className="text-[11px] text-gray-400 mt-1">
            Foto-Upload ist nur bei gespeicherten Akten möglich.
          </p>
        )}

        {/* Vorschau hochgeladener Fotos */}
        {daten.uebergabe_fotos_urls.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
            {daten.uebergabe_fotos_urls.map(url => (
              <div key={url} className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-square">
                <img
                  src={url}
                  alt="Übergabe-Foto"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeFoto(url)}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Fehlgeschlagene Uploads + Retry-Button */}
        {failedUploads.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {failedUploads.map(({ file, name }) => (
              <div
                key={name}
                className="flex items-center justify-between px-3 py-2 bg-red-50 border border-red-200 rounded-xl"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ImageOff className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <span className="text-[11px] text-red-700 truncate">{name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void uploadFoto(file).then(url => {
                      if (url) onChange({ uebergabe_fotos_urls: [...(daten.uebergabe_fotos_urls ?? []), url] });
                    });
                  }}
                  disabled={uploadingFiles.includes(name)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-[10px] font-semibold transition flex-shrink-0"
                >
                  <RefreshCw className="w-3 h-3" />
                  Erneut versuchen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
